import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { List } from '@wds/list';
import { Pagination } from '@wds/pagination';
import Filters from './Filters';
import ResponseCard from './ResponseCard';
import ResponseCardColdStart from './ResponseCardColdStart';
import CreateSuggestedThemeModal from './CreateSuggestedThemeModal';
import ResponsesSkeleton from './ResponsesSkeleton';
import FiveHundredError from '../../shared/FiveHundredError';
import OpenEndedCodingContext from '../../OpenEndedCodingContext';
import { useFilters, useFiltersActions, useResponses, useThemes, useThemesReuse, useThemesReuseActions } from '../../Context';
import { getPaginatedResponses, mergeSelectedIdsAndAutoGeneratedIds } from '../../lib';
import { ALL_FILTER_ID, ALL_FILTER_ID_BLANK, COLD_START_ENABLED, USE_CASE_NONE } from '../../constants';
import { getThemesPerResponse, calculateTrainingCounts } from '../AutoTagging/lib';
import LinkStyleButton from '../../shared/LinkStyleButton';
import { FILTER_BY_TAGGED_STATUS, SORT_BY } from '../../types';
export const COPY = {
  title: {
    id: 'OpenEndedCoding.Responses.title',
    defaultMessage: 'Responses ({responsesCount})',
    description: '[Type: Header][Vis: med] - Header for auto tagging'
  },
  titleStillLoading: {
    id: 'OpenEndedCoding.Responses.titleStillLoading',
    defaultMessage: 'Responses',
    description: '[Type: Header][Vis: med] - Header for auto tagging'
  },
  error: {
    id: 'OpenEndedCoding.Responses.error',
    defaultMessage: "We couldn't load your responses",
    description: '[Type: Header][Vis: med] - OEC load responses error'
  },
  manuallyTagged: {
    id: 'OpenEndedCoding.Responses.manuallyTagged',
    defaultMessage: 'Tagged manually',
    description: '[Type: Header][Vis: med] - number of responses that were tagged manually'
  },
  autoTagged: {
    id: 'OpenEndedCoding.Responses.autoTagged',
    defaultMessage: 'Auto-tagged',
    description: '[Type: Header][Vis: med] - number of responses that were auto-tagged'
  },
  untagged: {
    id: 'OpenEndedCoding.Responses.untagged',
    defaultMessage: 'Not tagged',
    description: '[Type: Header][Vis: med] - number of responses that are not tagged '
  },
  noResponsesHeader: {
    id: 'OpenEndedCoding.Responses.noResponsesHeader',
    defaultMessage: 'No responses found',
    description: '[Type: Header][Vis: high] - indicate that no responses are being displayed'
  },
  noResponsesSubheading: {
    id: 'OpenEndedCoding.Responses.noResponsesSubHeading',
    defaultMessage: 'No responses match this combination of filters.',
    description: '[Type: Header][Vis: med] - explain no responses are being displayed because of the current filters applied'
  },
  viewAllResponses: {
    id: 'OpenEndedCoding.Responses.viewAllResponses',
    defaultMessage: 'View all responses',
    description: '[Type: Header][Vis: high] - prompt user to view all the responses'
  }
};
const useStyles = createUseStyles({
  list: {
    // remove the default list item padding to maximize width
    // as the theme selector section has a reduced grid portion.
    '& [class^="wrenchListLabel"]': {
      padding: 0
    }
  }
});
const Responses = ({
  showCreateSuggestedThemeModal,
  setShowCreateSuggestedThemeModal
}) => {
  const classes = useStyles();
  const {
    filterResponses,
    setResponsesPage,
    handleCreateThemeForResponse
  } = useContext(OpenEndedCodingContext);
  const {
    responses,
    responsesCount,
    responsesLoading,
    responsesError,
    responsesPage,
    responsesPageSize,
    responsesTotal
  } = useResponses();
  const {
    setSuggestedThemesForResponse
  } = useThemesReuseActions();
  const {
    selectedUseCase,
    suggestedThemes
  } = useThemesReuse();
  const {
    themes
  } = useThemes();
  const {
    selectedThemes,
    searchInput,
    sortValue,
    filterByTaggedStatus,
    selectedSentiment
  } = useFilters();
  const {
    setMultiple: setMultipleFilterFields
  } = useFiltersActions();
  const paginatedResponses = getPaginatedResponses({
    responses,
    responsesPage
  });
  const [suggestedTheme, setSuggestedTheme] = useState(null);
  const [suggestedThemeResponseTextId, setSuggestedThemeResponseTextId] = useState(null);
  const themesPerResponse = getThemesPerResponse(themes);
  const counts = calculateTrainingCounts({
    responses: themesPerResponse,
    responsesTotal,
    themesTotal: themes.length
  });
  const [userAction, setUserAction] = useState(false);
  // execute the filter on user actions
  useEffect(() => {
    if (userAction) {
      filterResponses({
        search: searchInput,
        sortBy: sortValue,
        filterByTaggedStatus: filterByTaggedStatus,
        themeIds: mergeSelectedIdsAndAutoGeneratedIds(themes, selectedThemes),
        sentiments: selectedSentiment
      });
      setUserAction(false);
    }
  }, [userAction]);
  const handleClickViewAllResponses = () => {
    // Back to blank state - clear all of the filters
    setMultipleFilterFields({
      filterByTaggedStatus: FILTER_BY_TAGGED_STATUS.ALL,
      sortValue: SORT_BY.DATE,
      searchInput: '',
      selectedThemes: [ALL_FILTER_ID, ALL_FILTER_ID_BLANK, ...themes.map(theme => theme.id)],
      selectedSentiment: []
    });
    setUserAction(true);
  };
  const ResponsesComponent = () => {
    if (responsesLoading) {
      return _jsx(ResponsesSkeleton, {});
    }
    if (responsesError) {
      return _jsx(FiveHundredError, {
        title: t(COPY.error)
      });
    }
    if (responsesCount === 0) {
      return _jsxs(Box, {
        my: 8,
        children: [_jsx(Typography, {
          variant: "cardTitle",
          weight: "medium",
          align: "center",
          children: _jsx(T, {
            desc: COPY.noResponsesHeader
          })
        }), _jsx(Box, {
          mt: 4,
          children: _jsx(Typography, {
            align: "center",
            children: _jsx(T, {
              desc: COPY.noResponsesSubheading
            })
          })
        }), _jsx(Box, {
          display: "flex",
          width: "100%",
          justifyContent: "center",
          mt: 4,
          children: _jsx(LinkStyleButton, {
            onClick: handleClickViewAllResponses,
            children: _jsx(T, {
              desc: COPY.viewAllResponses
            })
          })
        })]
      });
    }
    return _jsxs(List, {
      dense: true,
      id: "responsesList",
      children: [paginatedResponses.map(response => _jsx(List.Item, {
        children: COLD_START_ENABLED && selectedUseCase !== USE_CASE_NONE ? _jsx(ResponseCardColdStart, {
          response: response,
          suggested: response.responseTextId in suggestedThemes ? suggestedThemes[response.responseTextId] : [],
          setSuggestedTheme: setSuggestedTheme,
          setSuggestedThemeResponseTextId: setSuggestedThemeResponseTextId,
          setShowCreateSuggestedThemeModal: setShowCreateSuggestedThemeModal
        }, response.responseTextId) : _jsx(ResponseCard, {
          response: response
        }, response.responseTextId)
      }, response.responseTextId)), _jsx(List.Item, {
        children: _jsx(Box, {
          pt: 4,
          children: _jsx(Pagination, {
            page: responsesPage,
            count: responsesCount,
            rowsPerPage: responsesPageSize,
            onChangePage: (_, page) => setResponsesPage(page),
            onChangeRowsPerPage: () => ''
          })
        })
      })]
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(Box, {
      py: 5,
      px: 6,
      className: classes.list,
      children: [_jsxs(Box, {
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        children: [_jsx(Typography, {
          component: "h5",
          variant: "cardTitle",
          children: _jsx("strong", {
            children: (responsesLoading || responsesError) && responsesCount === 0 ? _jsx(T, {
              desc: COPY.titleStillLoading
            }) : _jsx(T, {
              desc: COPY.title,
              values: {
                responsesCount
              }
            })
          })
        }), !responsesLoading && !responsesError && responsesCount !== 0 && _jsx(Typography, {
          variant: "bodySm",
          "data-testid": "TaggedTally",
          children: `${t(COPY.manuallyTagged)}  ${counts.manualTaggedCount}  •  ${t(COPY.autoTagged)}  ${counts.autoTaggedCount}  •  ${t(COPY.untagged)}  ${counts.untaggedCount}`
        })]
      }), _jsx(Filters, {}), ResponsesComponent()]
    }), _jsx(CreateSuggestedThemeModal, {
      show: showCreateSuggestedThemeModal,
      onClose: () => {
        setShowCreateSuggestedThemeModal(false);
        // clear values
        setSuggestedTheme(null);
        setSuggestedThemeResponseTextId(null);
      },
      onCreate: () => {
        if (suggestedTheme && suggestedThemeResponseTextId) {
          handleCreateThemeForResponse(suggestedTheme, suggestedThemeResponseTextId);
          // remove the suggested theme
          const suggested = suggestedThemes[suggestedThemeResponseTextId];
          if (suggested) {
            setSuggestedThemesForResponse(suggested.filter(s => s !== suggestedTheme), suggestedThemeResponseTextId);
          }
        }
        // clear values
        setSuggestedTheme(null);
        setSuggestedThemeResponseTextId(null);
      }
    })]
  });
};
export default Responses;